import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleLayout from '@/components/feature/ArticleLayout';

export default function FaqPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How many categories should I start with?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most new Mulebuy spreadsheet users begin with 3-4 core categories — typically Shoes, T-Shirts, and Hoodies/Sweaters. Master these before gradually expanding to all fourteen categories.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the ideal column structure?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The recommended Mulebuy spreadsheet column structure includes: Product ID, Name, Category, Supplier, Unit Price, Bulk Price, Size Range, Quality Score (1-5), Lead Time (days), Stock Status, and Notes.',
        },
      },
      {
        '@type': 'Question',
        name: 'How often should I update prices?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'High-turnover categories like Shoes and T-Shirts benefit from weekly updates. Seasonal categories can be updated bi-weekly. Niche categories typically require monthly review.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I share my spreadsheet with team members?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — the Mulebuy spreadsheet framework supports multi-user collaboration. Establish clear editing permissions by category and maintain a changelog sheet.',
        },
      },
      {
        '@type': 'Question',
        name: 'What if my spreadsheet becomes too large?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Archive discontinued products quarterly. Move inactive items to an archive sheet. This keeps your active Mulebuy spreadsheet lean while preserving historical data for trend analysis.',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <ArticleLayout
          heroImage="https://readdy.ai/api/search-image?query=Open%20book%20with%20question%20mark%20shaped%20paper%20cutout%20casting%20shadow%20on%20light%20desk%20surface%2C%20soft%20natural%20daylight%2C%20minimalist%20knowledge%20and%20learning%20concept%2C%20clean%20editorial%20photography%2C%20warm%20ivory%20and%20cream%20tones%2C%20thoughtful%20composition&width=1920&height=900&seq=mulebuy-faq-hero-v2&orientation=landscape"
          badge="FAQ"
          title="Mulebuy Spreadsheet FAQ & Troubleshooting"
          date="June 2025"
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">Frequently Asked Questions</h2>

          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">How many categories should I start with?</h3>
              <p className="text-foreground-600 leading-relaxed">
                Most new Mulebuy spreadsheet users begin with 3-4 core categories — typically <strong className="text-foreground-800">Shoes</strong>, <strong className="text-foreground-800">T-Shirts</strong>, and <strong className="text-foreground-800">Hoodies/Sweaters</strong>. Master these before gradually expanding to <strong className="text-foreground-800">Jackets</strong>, <strong className="text-foreground-800">Accessories</strong>, and eventually all fourteen categories including <strong className="text-foreground-800">OtherStuff</strong>.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">What is the ideal column structure?</h3>
              <p className="text-foreground-600 leading-relaxed">
                The recommended Mulebuy spreadsheet column structure includes: Product ID, Name, Category, Supplier, Unit Price, Bulk Price, Size Range, Quality Score (1-5), Lead Time (days), Stock Status, and Notes. Adjust category-specific columns for <strong className="text-foreground-800">Bags</strong> (capacity), <strong className="text-foreground-800">Perfumes</strong> (scent profile), and <strong className="text-foreground-800">Jersey</strong> (team specs).
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">How often should I update prices?</h3>
              <p className="text-foreground-600 leading-relaxed">
                High-turnover categories like <strong className="text-foreground-800">Shoes</strong> and <strong className="text-foreground-800">T-Shirts</strong> benefit from weekly price updates. Seasonal categories like <strong className="text-foreground-800">Sets</strong> and <strong className="text-foreground-800">Headwear</strong> can be updated bi-weekly. Niche categories including <strong className="text-foreground-800">Belts</strong> and <strong className="text-foreground-800">Underwear/Underpants</strong> typically require monthly review.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">Can I share my spreadsheet with team members?</h3>
              <p className="text-foreground-600 leading-relaxed">
                Yes — the Mulebuy spreadsheet framework supports multi-user collaboration. Establish clear editing permissions by category: assign <strong className="text-foreground-800">Pants/Shorts</strong> to one team member, <strong className="text-foreground-800">Accessories</strong> to another, and maintain a changelog sheet to track all modifications across categories.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">What if my spreadsheet becomes too large?</h3>
              <p className="text-foreground-600 leading-relaxed">
                Archive discontinued products quarterly. Move items from <strong className="text-foreground-800">Shoes</strong>, <strong className="text-foreground-800">Jackets</strong>, and <strong className="text-foreground-800">OtherStuff</strong> that have not been active for 90 days to an archive sheet. This keeps your active Mulebuy spreadsheet lean and fast while preserving historical data for trend analysis.
              </p>
            </div>
          </div>
        </ArticleLayout>
      </main>
      <Footer />
    </>
  );
}